class CustomizationForm {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('.customization-form__form');
    this.submitBtn = section.querySelector('.customization-form__submit');
    this.whatsappNumber = section.dataset.whatsappNumber;
    this.messagePrefix = this.section.dataset.messagePrefix || "Hi! I'd like to customize a product.";
    this._captureTimer = null;
    this._lastCaptured = null;

    this.init();
  }

  init() {
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Bind blur capture + error clearing on all inputs
    this.section.querySelectorAll('.customization-form__input, .customization-form__textarea').forEach(input => {
      input.addEventListener('blur', () => this.captureLead());
      input.addEventListener('input', () => {
        const field = input.closest('.customization-form__field');
        if (field) {
          field.classList.remove('customization-form__field--error');
          const error = field.querySelector('.customization-form__error');
          if (error) error.classList.remove('is-visible');
        }
      });
    });

    // Smooth scroll for anchor links
    document.querySelectorAll('a[href="#customization-form"]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.getElementById('customization-form');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  handleSubmit(e) {
    e.preventDefault();
    if (!this.validate()) return;

    // Final capture before opening WhatsApp
    this.captureLead(true);

    const message = this.buildMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${this.whatsappNumber}?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  validate() {
    let isValid = true;
    const requiredFields = this.section.querySelectorAll('[data-required="true"]');

    requiredFields.forEach(input => {
      const field = input.closest('.customization-form__field');
      const error = field?.querySelector('.customization-form__error');

      if (!input.value.trim()) {
        isValid = false;
        field?.classList.add('customization-form__field--error');
        if (error) {
          error.textContent = 'This field is required';
          error.classList.add('is-visible');
        }
      }

      // Email format validation
      if (input.dataset.field === 'email' && input.value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(input.value.trim())) {
          isValid = false;
          field?.classList.add('customization-form__field--error');
          if (error) {
            error.textContent = 'Please enter a valid email';
            error.classList.add('is-visible');
          }
        }
      }
    });

    return isValid;
  }

  buildMessage() {
    const productTitle = this.section.dataset.productTitle || 'Product';
    const productVariant = this.getSelectedVariant();
    const productUrl = this.section.dataset.productUrl || window.location.href;

    let message = `${this.messagePrefix}\n\n`;
    message += `Product: ${productTitle}\n`;

    if (productVariant) {
      message += `Variant: ${productVariant}\n`;
    }

    message += `URL: ${productUrl}\n\n`;

    // Only include fields that have values
    const fieldMap = {
      name: 'Name',
      email: 'Email',
      design: 'Design',
      text: 'Text to Print'
    };

    const lines = [];
    for (const [key, label] of Object.entries(fieldMap)) {
      const el = this.section.querySelector(`[data-field="${key}"]`);
      const value = el?.value?.trim();
      if (value) {
        lines.push(`${label}: ${value}`);
      }
    }

    if (lines.length > 0) {
      message += lines.join('\n') + '\n\n';
    }

    message += `Looking forward to hearing from you!`;
    return message;
  }

  getSelectedVariant() {
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedInputs = variantSelects.querySelectorAll('input[type="radio"]:checked');
      if (selectedInputs.length > 0) {
        return Array.from(selectedInputs).map(input => input.value).join(' / ');
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      return variantParam;
    }

    return null;
  }

  getFormData() {
    const getVal = (key) => {
      const el = this.section.querySelector(`[data-field="${key}"]`);
      return el?.value?.trim() || '';
    };

    return {
      name: getVal('name'),
      email: getVal('email'),
      design: getVal('design'),
      text: getVal('text'),
      productTitle: this.section.dataset.productTitle || '',
      productUrl: this.section.dataset.productUrl || '',
      variant: this.getSelectedVariant() || ''
    };
  }

  captureLead(force) {
    const formId = this.section.dataset.formId;
    if (!formId) return;

    const data = this.getFormData();
    if (!data.name || !data.email) return;

    // Deduplication — skip if same data was already sent
    const fingerprint = JSON.stringify({ n: data.name, e: data.email, d: data.design, t: data.text });
    if (fingerprint === this._lastCaptured && !force) return;

    const send = () => {
      const params = new URLSearchParams();

      const entryMap = {
        name: this.section.dataset.entryName,
        email: this.section.dataset.entryEmail,
        productTitle: this.section.dataset.entryProduct,
        variant: this.section.dataset.entryVariant,
        productUrl: this.section.dataset.entryUrl,
        design: this.section.dataset.entryDesign,
        text: this.section.dataset.entryText
      };

      for (const [key, entryId] of Object.entries(entryMap)) {
        if (entryId && data[key]) {
          params.append(entryId, data[key]);
        }
      }

      const timestampEntry = this.section.dataset.entryTimestamp;
      if (timestampEntry) {
        params.append(timestampEntry, new Date().toISOString());
      }

      fetch(`https://docs.google.com/forms/d/e/${formId}/formResponse`, {
        method: 'POST',
        mode: 'no-cors',
        body: params
      });

      this._lastCaptured = fingerprint;
    };

    if (force) {
      clearTimeout(this._captureTimer);
      send();
    } else {
      clearTimeout(this._captureTimer);
      this._captureTimer = setTimeout(send, 5000);
    }
  }
}

// Initialize all customization forms on the page
function initCustomizationForms() {
  const sections = document.querySelectorAll('.customization-form');
  sections.forEach(section => {
    if (!section.dataset.initialized) {
      new CustomizationForm(section);
      section.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomizationForms);
} else {
  initCustomizationForms();
}

// Re-initialize when section is loaded in theme editor
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('.customization-form')) {
    initCustomizationForms();
  }
});
